import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { FileField } from "models/interfaces";
import strings from "global/constants/StringConstants";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import { pressableProps } from "../mobileA11y";
import OptionTypeIcon from "../../../../assets/icons/MobileFieldIcons/OptionType.svg";
import OptionFormattingIcon from "../../../../assets/icons/MobileFieldIcons/OptionFormatting.svg";
import OptionFieldDataIcon from "../../../../assets/icons/MobileFieldIcons/OptionFieldData.svg";
import OptionMoreIcon from "../../../../assets/icons/MobileFieldIcons/OptionMore.svg";

// Field types that expose text "Formatting" (font family/size/weight/color) —
// mirrors the desktop PropertiesDrawer, where the Formatting accordion renders
// only for these types.
const FORMATTING_TYPES = [
  strings.text,
  strings.email,
  strings.name,
  strings.companyName,
  strings.title,
];

/**
 * Bottom toolbar shown in the mobile editor when a placed field is selected.
 * Replaces the field-type palette (`FieldsPanelMobile`) with the options for the
 * currently selected field (Figma "Fields options", node 1345-6080):
 * Type · Formatting · Field Data · more.
 *
 * The individual option panels are provided screen-by-screen by the designer;
 * each chip surfaces through an optional callback so they can be wired as those
 * screens land.
 */
export type FieldOption = "type" | "formatting" | "fieldData";

const MobileFieldOptionsBar = ({
  field,
  activeOption,
  onOptionClick,
  onMoreClick,
  containerSx,
}: {
  field: FileField;
  activeOption?: FieldOption | null;
  onOptionClick?: (option: FieldOption, field: FileField) => void;
  onMoreClick?: (anchor: HTMLElement, field: FileField) => void;
  containerSx?: any;
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const classes = MobileSignRequestStyles(theme);

  // Render the option chips according to the selected field's type, like the
  // desktop properties panel: "Type" and "Field Data" apply to every field,
  // while "Formatting" only shows for text-family fields.
  const normalizedType = (field.type || "").replace(/_/g, " ").toLowerCase();
  const showFormatting = FORMATTING_TYPES.some(
    (t) => t.replace(/_/g, " ").toLowerCase() === normalizedType,
  );

  const options: { key: FieldOption; label: string; icon: string }[] = [
    { key: "type", label: "Type", icon: OptionTypeIcon },
    ...(showFormatting
      ? [
          {
            key: "formatting" as FieldOption,
            label: "Formatting",
            icon: OptionFormattingIcon,
          },
        ]
      : []),
    { key: "fieldData", label: "Field Data", icon: OptionFieldDataIcon },
  ];

  return (
    <Box
      role="toolbar"
      aria-label="Field options"
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: 100,
        bgcolor: m.surface.white,
        borderTop: `1px solid ${m.surface[100]}`,
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 1,
        ...containerSx,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {options.map((opt) => {
          const isActive = activeOption === opt.key;
          return (
            <Box
              key={opt.key}
              {...pressableProps(() => onOptionClick?.(opt.key, field), {
                label: opt.label,
                selected: isActive,
              })}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
                cursor: "pointer",
                userSelect: "none",
                bgcolor: m.surface.white,
                border: `1px solid ${isActive ? m.ePurple[500] : m.grey[50]}`,
                borderRadius: "12px",
                p: 1,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  background: classes.primaryGradient50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <img
                  src={opt.icon}
                  alt={`${opt.label} option icon`}
                  width={16}
                  height={16}
                  aria-hidden="true"
                />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: m.base[900],
                  whiteSpace: "nowrap",
                }}
              >
                {opt.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      <Box
        {...pressableProps((e) => onMoreClick?.(e.currentTarget as HTMLElement, field), {
          label: "More field options",
          hasPopup: "menu",
        })}
        sx={{
          flexShrink: 0,
          width: 24,
          height: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          mr: 0.5,
        }}
      >
        <img src={OptionMoreIcon} alt="" aria-hidden="true" width={24} height={24} />
      </Box>
    </Box>
  );
};

export default MobileFieldOptionsBar;
